/**
 * Sentry Error Monitoring Configuration
 *
 * Initializes Sentry for production error tracking.
 * Only active in production builds.
 */

// Note: Import dynamically to avoid bundling in development
let Sentry: typeof import('@sentry/react') | null = null;

export interface SentryConfig {
  dsn?: string;
  environment?: string;
  release?: string;
  tracesSampleRate?: number;
}

/**
 * Initialize Sentry error monitoring
 * Only initializes in production when DSN is configured
 */
export async function initSentry(config?: SentryConfig): Promise<void> {
  // Only initialize in production
  if (!import.meta.env.PROD) {
    console.log('[Sentry] Skipping initialization in development mode');
    return;
  }

  const dsn = config?.dsn || import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('[Sentry] No DSN configured. Error monitoring disabled.');
    return;
  }

  try {
    // Dynamic import to avoid bundling in development
    Sentry = await import('@sentry/react');

    Sentry.init({
      dsn,
      environment:
        config?.environment || import.meta.env.VITE_ENVIRONMENT || 'production',
      release: config?.release || import.meta.env.VITE_APP_VERSION,
      tracesSampleRate: config?.tracesSampleRate ?? 0.1,

      // Only send errors, not all transactions
      beforeSend(event) {
        // Filter out non-error events in production
        if (!event.exception) {
          return null;
        }
        return event;
      },

      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Replay settings (session replay for debugging)
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.1,
    });

    console.log('[Sentry] Initialized successfully');
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
  }
}

/**
 * Capture an exception manually
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): void {
  if (Sentry) {
    Sentry.captureException(error, {
      extra: context,
    });
  } else {
    console.error('[Sentry] Not initialized. Error:', error, context);
  }
}

/**
 * Capture a message/log
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  if (Sentry) {
    Sentry.captureMessage(message, level);
  } else {
    console.log(`[Sentry] ${level.toUpperCase()}: ${message}`);
  }
}

/**
 * Set user context for error tracking
 */
export function setUser(
  user: { id: string; email?: string; username?: string } | null
): void {
  if (Sentry) {
    Sentry.setUser(user);
  }
}

/**
 * Add breadcrumb for debugging context
 */
export function addBreadcrumb(breadcrumb: {
  category: string;
  message: string;
  level?: 'info' | 'warning' | 'error';
  data?: Record<string, unknown>;
}): void {
  if (Sentry) {
    Sentry.addBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message,
      level: breadcrumb.level || 'info',
      data: breadcrumb.data,
    });
  }
}

/**
 * Create a Sentry error boundary wrapper
 * Use this to wrap critical components
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ReactNode
): React.ComponentType<P> {
  if (Sentry) {
    return Sentry.withErrorBoundary(Component, {
      fallback: fallback as React.ReactElement,
    });
  }
  return Component;
}
